import {
    Component,
    ViewChild,
    Input,
    OnDestroy,
    OnInit,
    ElementRef,
    DoCheck,
    HostListener
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormArray,
    Validators,
    ValidatorFn,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';

import { Subscription, Observable, of, combineLatest, filter, Observer } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { cloneDeep, union, uniq, isEqual, findIndex } from 'lodash';
import { MessageService, MenuItem } from 'primeng/api';
import moment, { Moment } from 'moment-timezone';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import {
    twitterValidation,
    facebookValidation,
    instaValidation,
    youtubeValidation,
    redditValidation,
    subRedditValidation,
    tiktokValidation,
    minLengthValidation,
    mustNotBeOperator,
    HandleHelper,
    linkedinValidation,
    blueskyValidation
} from '../../../../Common/Forms/helpers/handle.helper';
import { trimLongStringWithEllipsis } from '../../../../Core/utils';
import { MediaSubscriptionApiService } from '../../../../api/mediaSubscriptions/media-subscription.api.service';
import { mediaTypeToNetwork } from '@Social/Core/enums/network-type.enum';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { RelativeTimePipe } from '../../../../Common/Misc/pipes/RelativeTime/relative-time.pipe';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { MediaSubscriptionService } from '../../../../Monitor/Core/services/media-subscription.service';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { ProgressChartModel, ProgressChartOptionsModel } from '../../../../Common/Charts/models/progressChart/progress-chart.model';
import { Theme } from '../../models/theme.model';
import { NetworkTypeEnum, networkOptionsOrder as networkOrder  } from '../../../Core/enums/network-type.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MediaSubscriptionStatusEnum as StatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { networkTypeToMediaSubTypeMap, mediaSubTypeToNetworkTypeMap } from '../../models/network-type-to-media-sub-type.map';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ThemeService } from '../../services/theme.service';
import { AgentService } from '../../services/agent.service';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { QuotaInformation } from '../../../../api/mediaSubscriptions/models/quota-information.api.model';
import { abbreviateNumber } from '../../../../Common/Misc/helpers/formatting/number.helper';
import { ChartStylesModel } from '../../../../Common/Charts/models/chart-styles.model';
import { TextInputDialogComponent } from '../../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '../../../../Common/Dialogs/models/text-input-dialog.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { BlacklistedHandle } from '../../../../api/mediaSubscriptions/models/media-subscription.api.model';
import { MediaSubscriptionMediaTypeEnum } from '../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MonitoringQuotaTypesEnum } from '../../../../api/mentions/enum/monitoring-quota-types.enum';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { AgentSearchSettingsComponent } from '../AgentSearchSettings/agent-search-settings.component';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { getMonitoringDateQueryInputTopicOptions } from '@Monitor/Shared/models/monitoring-date-query-input-options';
import { SocialTopicReplayDialogComponent } from '../SocialTopicReplayDialog/social-topic-replay-dialog.component';
import { SearchRangeModel } from '@Monitor/Core/models/search-range.model';
import { AgentHistoryComponent } from '../AgentHistory/agent-history.component';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { MediaTypeRequest } from '../../../../Monitor/Shared/models/media-subscription.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';


export interface Handle {
    type: NetworkTypeEnum;
    handleValues: string[];
    blacklistedHandles?: BlacklistedHandle[];
};

export enum AgentWidgetMode {
    create,
    edit,
}
// this is the order of RHP tab
enum AgentWidgetTab {
    additionalSearchSettings,
    details,
    postVolumeEstimate,
    replayHistory
}

/**
 * This component is responsible for the creation and modification of agents (social topics)
 * It's also used as the base class for ArchiveSearchWidgetComponent,
 * which should be considered before making changes
 */
@Component({
    selector: 'agpr-agent-widget',
    templateUrl: './agent-widget.component.html',
    styleUrls: ['./agent-widget.component.scss'],
    providers: [],
    standalone: false
})
export class AgentWidgetComponent extends AbstractDialogComponent implements OnInit, OnDestroy, DoCheck {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @Input() mode: AgentWidgetMode = AgentWidgetMode.create;
    @Input() set agentModel(agent: MediaSubscription) {
        if (!agent) {
            return;
        }
        this.agent = agent.clone();
        this.agent.status = agent.status;
        this.agentSummary = agent.clone();
        this.agentSummary.status = agent.status;
        if (this.agent?.isModeratedForHandles) {
            this.getModeratedHandles();
        }
    }

    @Input() theme: Theme | null;

    @ViewChild('scrollpanel') scrollPanel: ScrollPanelComponent;
    @ViewChild('agentSearchSettings') agentSearchSettings: AgentSearchSettingsComponent;

    readonly id: string = 'agentWidget';
    readonly buttonTypes = ButtonTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly agentWidgetMode = AgentWidgetMode;
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;
    readonly networkType = NetworkTypeEnum;
    readonly mediaSubscriptionMediaType = MediaSubscriptionMediaTypeEnum;

    readonly twitterValidation = twitterValidation;
    readonly facebookValidation = facebookValidation;
    readonly instaValidation = instaValidation;
    readonly youtubeValidation = youtubeValidation;
    readonly redditValidation = redditValidation;
    readonly subRedditValidation = subRedditValidation;
    readonly tiktokValidation = tiktokValidation;
    readonly linkedinValidation = linkedinValidation;
    readonly blueskyValidation = blueskyValidation;
    readonly minLengthValidation = minLengthValidation;
    readonly mustNotBeOperator = mustNotBeOperator;

    readonly saveActions: MenuItem[] = [
        {
            label: this.localizationService.transform('Social.agentWidget.button.save.changes'),
            command: () => this.save()
        },
        {
            label: this.localizationService.transform('Social.agentWidget.button.saveAs'),
            command: () => this.openSaveAsDialog()
        }
    ];

    readonly quotaEstimateChartOptions: ProgressChartOptionsModel = {
        barHeight: 20,
        usesLegend: true,
    };

    headerTitle: string = 'Social.agentWidget.title.create';
    loading: boolean = false;

    partiallyModeratedNetworks: NetworkTypeEnum[];

    agent: MediaSubscription;
    agentSummary: MediaSubscription;
    agents: MediaSubscription[] = [];
    allowedNetworks: NetworkTypeEnum[] = [];
    notAllowedNetworks: NetworkTypeEnum[] = [];
    cachedAgent: MediaSubscription;
    saveErrorTooltip: string;

    handleExpanded: boolean = true;
    showEstimate: boolean = false;
    hasEstimate: boolean;
    hasSamplePosts: boolean;
    loadingEstimate: boolean = false;

    quotaEstimateChart: ProgressChartModel;
    quotaInfo: QuotaInformation;
    mentions: Mention[] = [];
    totalUsageCount: number = 0;
    estimatedTotalUsageCount: number = 0;
    estimatedAgentUsageCount: number = 0;
    estimatedAgentUsageRange: string = '';

    themeOptionFetcher: Observable<Theme[]>;

    agentForm: FormGroup;

    selectedTabIndex: number = AgentWidgetTab.additionalSearchSettings;

    searchRange: SearchRangeModel = null;
    replayDate: DateQueryModel = DateQueryModel.getLast24HoursQuery();
    replayDateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();
    isDirty: boolean = false;
    settingUpReplay: boolean = false;
    isCustomReplay: boolean = false;
    setUpReplayAfter: boolean = false;
    containX: boolean = false;
    isQuotaExceeded: boolean = false;
    onlyNotKeywords: boolean = false;
    timeZone: string;

    @ViewChild('agentHistory') agentHistory: AgentHistoryComponent;

    protected relativeTimePipe: RelativeTimePipe;
    protected subscriptions: Subscription[] = [];

    private allThemeMap = new Map<Pick<Theme, 'id'>, Theme>();

    constructor(
        public coreDialogService: CoreDialogService,
        protected agentService: AgentService,
        protected mediaSubscriptionService: MediaSubscriptionService,
        protected localizationService: LocalizationService,
        protected themeService: ThemeService,
        protected messageService: MessageService,
        protected monitorConfigService: MonitorConfigService,
        protected mediaSubscriptionApiService: MediaSubscriptionApiService,
        protected currentUserStateService: CurrentUserStateService,
        protected elementRef: ElementRef
    ) {
        super(coreDialogService, 'AgentWidget');

        this.themeOptionFetcher = this.themeService.all$;
        this.relativeTimePipe = new RelativeTimePipe(this.localizationService);

        this.agentForm = this.setDefaultForm();
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (this.isDirty) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.agentService.all$,
                this.monitorConfigService.getAvailableSocialMediaTypes()
            ]).subscribe(([agents, mediaTypes]) => {

                if (!agents || !mediaTypes) {
                    return;
                }

                this.agents = agents;
                this.allowedNetworks = mediaTypes
                    .map(media => mediaTypeToNetwork.get(media))
                    .filter(x => !!x);

                if (this.allowedNetworks.includes(NetworkTypeEnum.reddit) && !this.allowedNetworks.includes(NetworkTypeEnum.subReddit)) {
                    this.allowedNetworks.push(NetworkTypeEnum.subReddit);
                }
                this.notAllowedNetworks = networkOrder.filter(network => !this.allowedNetworks.includes(network));

                this.handles.clear();

                switch (this.mode) {
                    case AgentWidgetMode.create:
                        this.headerTitle = 'Social.agentWidget.title.create';
                        this.agent = new MediaSubscription();
                        // @HACK fix with different model
                        this.agent.isAgent = true;
                        if (!this.theme) {
                            console.error('Must provide a theme when agent-widget is in create mode');
                            return;
                        }


                        // add default handles based on provisioned networks
                        networkOrder.forEach(network => {
                            if (this.allowedNetworks.includes(network)) {
                                this.handles.push(
                                    new FormGroup({
                                        handleValues: new FormControl([]),
                                        type: new FormControl(network)
                                    },
                                    {
                                        validators: this.uniqueHandleValidator()
                                    })
                                );
                            }
                        });

                        this.agentForm.patchValue({
                            name: this.findDefaultAgentName(this.agents),
                            general: {
                                section: {
                                    networks: this.allowedNetworks,
                                    keywords: new IncludeExcludeModel(),
                                },
                            },
                            status: StatusEnum.ACTIVE,
                            themes: [this.theme.id]
                        });
                        this.agentSummary = this.getAgent();
                        break;

                    case AgentWidgetMode.edit:
                        this.headerTitle = 'Social.agentWidget.title.edit';
                        if (!this.agent) {
                            console.error('Must provide a agent when agent-widget is in edit mode');
                            return;
                        }

                        this.setAgentForm();
                        break;

                    default:
                        console.error('Agent-widget invalid mode');
                        break;
                }

                this.cachedAgent = this.getAgent().clone();
                this.agentChanged();
            }),
            this.mediaSubscriptionService.getQuotaInfo(MonitoringQuotaTypesEnum.SOCIAL_LISTENING).subscribe(quota => {
                if (quota) {
                    this.quotaInfo = quota;
                    this.totalUsageCount = this.quotaInfo.currentCount;
                    this.estimatedTotalUsageCount = this.totalUsageCount / moment().date() * (moment().daysInMonth() - moment().date());
                }
            }),
            this.mediaSubscriptionService.getIsQuotaExceeded(MonitoringQuotaTypesEnum.SOCIAL_LISTENING).subscribe(isQuotaExceeded => this.isQuotaExceeded = isQuotaExceeded ?? false),
            this.agentForm.get('general').valueChanges.subscribe(() => {
                this.getEstimate();
            }),
            this.monitorConfigService.getSearchRange().pipe(filter(x => !!x)).subscribe(
                searchRange => {
                    this.searchRange = cloneDeep(searchRange);
                    this.searchRange.from.setMonth(this.searchRange.from.getMonth() + 1);
                }
            ),
            this.agentForm.get('general.section.networks').valueChanges.subscribe(networks => {
                if (this.settingUpReplay) {
                    networks = [NetworkTypeEnum.twitter];
                    if (!isEqual(this.networks.value, [NetworkTypeEnum.twitter])) {
                        this.networks.patchValue(networks);
                    }
                }
                if (!networks.includes(NetworkTypeEnum.twitter)) {
                    this.setUpReplayAfter = false;
                }
                if (!networks.includes(NetworkTypeEnum.reddit)) {
                    // remove sub reddit if reddit is not selected
                    const index = networks.indexOf(NetworkTypeEnum.subReddit);
                    if (index !== -1) {
                        networks.splice(index, 1);
                    }
                } else {
                    // add sub reddit if reddit is selected
                    if (!networks.some(network => network === NetworkTypeEnum.subReddit)) {
                        networks.push(NetworkTypeEnum.subReddit);
                    }
                }

                // after remove an item, new this.handles index is 1 less than old index
                let numOfRemovedItems = 0;
                // remove handles that don't have network selections
                this.handles.value.forEach(
                    (handle: Handle, index: number) => {
                        if (!networks.includes(handle.type)) {
                            this.handles.removeAt(index - numOfRemovedItems);
                            numOfRemovedItems ++;
                        }
                    }
                );

                // add missing handles that have network selections
                networks.forEach(network => {
                    if (!this.handles.value.find(handle => handle.type === network)) {
                        this.handles.push(
                            new FormGroup({
                                handleValues: new FormControl([]),
                                type: new FormControl(network)
                            },
                            {
                                validators: this.uniqueHandleValidator()
                            })
                        );
                    }
                });

                this.handles.controls.sort((a, b) => {
                    return networkOrder.indexOf(a.get('type').value) - networkOrder.indexOf(b.get('type').value);
                });
            }),
            this.agentForm.get('general.handles').valueChanges.subscribe(handles => {
                for (const handle of handles) {
                    this.sanitizeHandles(handle.type, handle.handleValues);
                }
            }),
            this.agentForm.get('themes').valueChanges.subscribe(themes => {
                if (themes.length === 0) {
                    this.status.patchValue(StatusEnum.PAUSED);
                    this.status.disable();
                } else {
                    this.status.enable();
                }
            }),
            this.agentForm.valueChanges.subscribe(() => {
                this.agentSummary = this.getAgent();
                this.agentChanged();
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.timeZone = preferences?.timeZone;
                }
            ),
            this.themeService.all$.subscribe((themes) => {
                this.allThemeMap = themes.reduce((acc, theme) => {
                    acc.set(theme.id, theme);
                    return acc;
                }, new Map());
            })
        );
        this.partiallyModeratedNetworks = this.agent?.partiallyModeratedMedias.map(net => mediaSubTypeToNetworkTypeMap.get(net));
    }

    private setAgentForm() {
        const agentNetworks: NetworkTypeEnum[] = this.agent.mediaTypeRequests
            .map(x => mediaSubTypeToNetworkTypeMap.get(x.mediaType))
            .filter(network => this.allowedNetworks.includes(network));

        if (agentNetworks.includes(NetworkTypeEnum.subReddit) && !agentNetworks.includes(NetworkTypeEnum.reddit)) {
            agentNetworks.push(NetworkTypeEnum.reddit);
        }

        if (agentNetworks.includes(NetworkTypeEnum.reddit) && !agentNetworks.includes(NetworkTypeEnum.subReddit)) {
            agentNetworks.push(NetworkTypeEnum.subReddit);
        }

        const agentHandles: Handle[] = this.agent.mediaTypeRequests
            .filter(request => {
                const networks = mediaSubTypeToNetworkTypeMap.get(request.mediaType);
                return this.allowedNetworks.includes(networks);
            })
            .map(media => {
                return <Handle>{
                    handleValues: media.handles,
                    type: mediaSubTypeToNetworkTypeMap.get(media.mediaType),
                    blacklistedHandles: media.blacklistedHandles
                };
            }).sort((a, b) => {
                return networkOrder.indexOf(a.type) - networkOrder.indexOf(b.type);
            });
        // add handles based on network selection
        agentNetworks.forEach(network => {
            if (!this.handles.controls.find(control => control.value.type === network)) {
                this.handles.push(
                    new FormGroup({
                        handleValues: new FormControl([]),
                        type: new FormControl(network),
                        blacklistedHandles: new FormControl([])
                    },
                    {
                        validators: this.uniqueHandleValidator()
                    })
                );
            }
        });

        this.agentForm.patchValue({
            name: this.agent.name,
            general: {
                section: {
                    networks: agentNetworks,
                    keywords: new IncludeExcludeModel({
                        includeAll: this.agent.keywords.and,
                        include: this.agent.keywords.or,
                        exclude: this.agent.keywords.not
                    })
                },
                handles: agentHandles
            },
            status: this.agent.status,
            themes: union(this.theme?.id, this.agent.themeIds)
        });

        this.handles.controls.sort((a, b) => {
            return networkOrder.indexOf(a.get('type').value) - networkOrder.indexOf(b.get('type').value);
        });

        if (this.settingUpReplay && !this.isCustomReplay) {
            this.disableForm();
        } else {
            this.enableForm();
        }
    }

    setDefaultForm(): FormGroup {
        return new FormGroup({
            name: new FormControl('', [
                Validators.required,
                this.uniqueNameValidator()
            ]),
            general: new FormGroup({
                section: new FormGroup({
                    networks: new FormControl<NetworkTypeEnum[]>([],
                        Validators.required
                    ),
                    keywords: new FormControl(new IncludeExcludeModel<string>(), [
                        this.onlyNotKeywordValidator(),
                        this.maxKeywordValidator(),
                        this.hashtagValidator(),
                        this.keywordCharValidator()
                    ]),
                }, {
                    validators: this.keywordCharValidatorTwitter()
                }),
                handles: new FormArray([]),
            }, {
                validators: this.noHandleOrKeywordValidator()
            }),
            status: new FormControl<StatusEnum>({ value: StatusEnum.PAUSED, disabled: false }, [
                this.mustIncludeThemeIfActive()
            ]),
            themes: new FormControl([])
        });
    }

    sanitizeHandles(type: NetworkTypeEnum, handles: string[]) {
        switch (networkTypeToMediaSubTypeMap.get(type)) {
            case MediaTypeEnum.FACEBOOK:
                handles.forEach((x, index, array) => {
                    array[index] = HandleHelper.sanitizeFacebookHandle(x);
                });
                break;
            case MediaSubscriptionMediaTypeEnum.twitter:
                handles.forEach((x, index, array) => {
                    array[index] = '@' + HandleHelper.sanitizeTwitterHandle(x);
                });
                break;
            case MediaTypeEnum.INSTAGRAM:
                handles.forEach((x, index, array) => {
                    array[index] = '@' + HandleHelper.sanitizeInstagramHandle(x);
                });
                break;
            case MediaTypeEnum.TIKTOK:
                handles.forEach((x, index, array) => {
                    array[index] = '@' + HandleHelper.sanitizeTikTokHandle(x);
                });
                break;
            case MediaTypeEnum.YOUTUBE:
                handles.forEach((x, index, array) => {
                    array[index] = '@' + HandleHelper.sanitizeYoutubeChannel(x);
                });
                break;
            case MediaTypeEnum.REDDIT:
                handles.forEach((x, index, array) => {
                    array[index] = 'u/' + HandleHelper.sanitizeRedditUser(x);
                });
                break;
            case MediaSubscriptionMediaTypeEnum.subReddit:
                handles.forEach((x, index, array) => {
                    array[index] = 'r/' + HandleHelper.sanitizeSubReddit(x);
                });
                break;
            case MediaTypeEnum.LINKEDIN:
                handles.forEach((x, index, array) => {
                    array[index] = HandleHelper.sanitizeLinkedInHandle(x);
                });
                break;
            case MediaTypeEnum.BLUESKY:
                handles.forEach((x, index, array) => {
                    array[index] = HandleHelper.sanitizeBlueskyHandle(x);
                });
                break;
        }
    }

    ngDoCheck(): void {
        this.saveErrorTooltip = !this.agentForm.valid ? this.localizationService.transform('Social.agentWidget.error.general.heading') + '\n' : '';

        if (this.section.errors?.['invalidCharKeywordTwitter']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keyword.invalidCharacter.twitter')}\n`;
        }
        if (this.keywords.errors?.['hashtagSymbol']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keyword.hashtag')}\n`;
        }
        if (this.keywords.errors?.['onlyNotKeywords']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.onlyNotKeywords')}\n`;
        }
        if (this.keywords.errors?.['maxKeyword']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keywordMaximum')}\n`;
        }
        if (this.keywords.errors?.['invalidCharKeyword']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keyword.invalidCharacter')}\n`;
        }
        if (this.name.errors?.['duplicateName']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.nameExists')}\n`;
        }
        if (this.name.errors?.['required']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.nameMinLength')}\n`;
        }
        if (this.networks.errors?.['required']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.noNetwork')}\n`;
        }
        if (this.handles?.controls?.some(x => x.errors?.['duplicateHandle'])) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.handle.duplicate')}\n`;
        }
        if (this.general.errors?.['noHandleAndKeyword']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keywordOrHandle')}\n`;
        }
        if (this.status.errors?.['mustIncludeThemeIfActive']) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.noTheme')}\n`;
        }
    }

    get name(): FormControl {
        return this.agentForm.get('name') as FormControl;
    }

    get general(): FormGroup {
        return this.agentForm.get('general') as FormGroup;
    }

    get section(): FormGroup {
        return this.agentForm.get('general.section') as FormGroup;
    }

    get networks(): FormControl {
        return this.agentForm.get('general.section.networks') as FormControl;
    }

    get keywords(): FormControl {
        return this.agentForm.get('general.section.keywords') as FormControl;
    }

    get handles(): FormArray {
        return this.agentForm.get('general.handles') as FormArray;
    }

    get themes(): FormControl {
        return this.agentForm.get('themes') as FormControl;
    }

    get status(): FormControl {
        return this.agentForm.get('status') as FormControl;
    }

    setAdditionalSearchSettings(settings: any) {
        if (!(settings instanceof Event)) {
            this.agent.samplePercentage = settings.sampleRate;

            this.agent.countryCodes = settings.countries ? settings.countries.map(country => country.countryCode) : [];
            this.agent.geoCircles = settings.geoCircles ? settings?.geoCircles.map(x => {
                return {
                    latitude: x.latitude,
                    longitude: x.longitude,
                    radius: x.radius,
                    radiusUnit: x.radiusUnit
                };
            }) : [];

            this.agent.languageFilter = new IncludeExcludeModel<string>({
                include: settings.languages?.include?.map(x => x['id']) ?? [],
                exclude:  settings.languages?.exclude?.map(x => x['id']) ?? []
            });

            if (settings.showVerifiedOptions) {
                this.agent.verifiedAuthor = settings.verifiedAuthor;
            } else {
                this.agent.verifiedAuthor = null;
            }
            this.agentSummary = this.getAgent();
        }

    }


    /**
     * Search agent list for default names and find the first unused name, starting at 001
     */
    findDefaultAgentName(agents: MediaSubscription[]): string {
        let newIndex = agents.length + 1;
        const themeNameIndices = agents
            .filter(agent => /^social topic [0-9]+$/g.test(agent.name.trim().toLowerCase()))
            .map(x => +x.name.split(' ')[2]);

        for (let i = 1; i <= agents.length; i++) {
            if (themeNameIndices.indexOf(i) === -1) {
                newIndex = i;
                break;
            }
        }

        return `${this.localizationService.transform('Social.agentWidget.title')} ${newIndex.toString().padStart(3, '0')}`;
    }

    handleSetUpReplayAfter(event) {
        this.setUpReplayAfter = event;
    }

    /**
     * Create or update the agent based on editMode
     */
    save() {
        if (!this.agentSearchSettings.isFormValidated()) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Social.agentWidget.create.validationError.heading',
                detail: 'Social.agentWidget.create.validationError.message'
            });

            return;
        };

        if (this.mode === AgentWidgetMode.create && !this.validateThemes()) {
            return;
        }

        const agent = this.getAgent();
        this.loading = true;

        const doReplayAfterSave: boolean = this.isDirty && this.setUpReplayAfter && this.networks.value?.includes(NetworkTypeEnum.twitter);

        switch(this.mode) {
            case AgentWidgetMode.create:
                this.agentService.create(agent).pipe(
                    tap((id) => agent.id = id),
                    switchMap(() => this.themeService.refreshAllManual()),
                    finalize(() => {
                        this.loading = false;
                        if (doReplayAfterSave) {
                            this.setAgentAndReplay(agent);
                        } else {
                            this.closeDialog(true);
                        }
                    })
                ).subscribe();

                break;
            case AgentWidgetMode.edit:
                this.agentService.update(agent).pipe(
                    switchMap(() => this.themeService.refreshAllManual()),
                    finalize(() => {
                        this.loading = false;
                        if (doReplayAfterSave) {
                            this.setAgentAndReplay(agent);
                        } else {
                            this.closeDialog(true);
                        }
                    })
                ).subscribe();

                break;
            default:
                console.error('Agent-widget invalid mode');
                break;
        }
    }

    setAgentAndReplay(agent: MediaSubscription) {
        this.mode = AgentWidgetMode.edit;
        this.agent = agent;
        this.setAgentForm();
        this.cachedAgent = this.getAgent().clone();
        this.agentSummary = this.getAgent().clone();
        this.setupReplay();
    }

    reactivateAll() {
        this.coreDialogService.open(
            ConfirmDialogComponent,
            {
                inputs: {
                    heading: 'Social.agentWidget.reactivateSave',
                    messageHtml: 'Social.agentWidget.reactivate.message',
                    okLabel: 'save',
                    cancelLabel: 'cancel',
                    width: 350
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.partiallyModeratedNetworks = [];
                            this.agent.isPartiallyModerated = false;
                            this.agent.isModeratedForKeywords = false;
                            this.agent.isModeratedForHandles = false;
                            this.agent.mediaTypeRequests.forEach(mediaTypeRequest => {
                                mediaTypeRequest.isModeratedForHandles = false;
                                mediaTypeRequest.isModeratedForKeywords = false;
                            });
                            this.save();
                        }
                    }
                }
            }
        );
    }

    // Check all themes for a new topic to be added to
    validateThemes() {
        const themesIds  = this.themes.value;
        let inValidTheme = null;

        for (const themeId of themesIds) {
            const theme = this.allThemeMap.get(themeId);
            if (theme && !this.validateTheme(theme)) {
                inValidTheme = theme;
                break;
            }
        }

        if (inValidTheme) {
            this.onThemeError(inValidTheme.name);
            return false;
        }
        return true;
    }

    /**
     * Open save as dialog, prompting user for new agent name
     */
    openSaveAsDialog() {
        if (!this.validateThemes()) {
            return;
        }

        const doReplayAfterSave: boolean = this.isDirty && this.setUpReplayAfter && this.networks.value?.includes(NetworkTypeEnum.twitter);
        const io: TextInputDialogModel = {
            inputs: {
                id: 'saveAsTopic.dialog',
                heading: 'Social.agentWidget.saveAsDialog.heading',
                description: 'Social.agentWidget.saveAsDialog.description',
                placeholder: 'Social.agentWidget.saveAsDialog.placeholder',
                okLabel: 'Social.agentWidget.button.save',
                observableFormValidator: (agentName: string) => {
                    return this.agentService.isNameDuplicated$(agentName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Social.agentWidget.error.nameExists'];
                            } else if (agentName?.length < 1) {
                                return ['Social.agentWidget.error.nameMinLength'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (newName) => {
                    const agent = this.getAgent();
                    agent.name = newName;
                    agent.id = null;

                    this.loading = true;

                    return this.agentService.createCopy(agent).pipe(
                        tap((id) => agent.id = id),
                        switchMap(() => this.themeService.refreshAllManual()),
                        finalize(() => {
                            this.loading = false;
                            if (doReplayAfterSave) {
                                this.setAgentAndReplay(agent);
                            } else {
                                this.closeDialog(true);
                            }
                        })
                    );
                },
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    /**
     * Constructs the agent object based on the current agentForm state
     */
    private getAgent(): MediaSubscription {
        const keywords: IncludeExcludeModel<string> = this.keywords.value;
        const mediaTypeRequests: MediaTypeRequest[] = this.networks.value.map((network: NetworkTypeEnum) => {
            return {
                mediaType: networkTypeToMediaSubTypeMap.get(network),
                handles: this.handles.value.find((handle: Handle) => handle.type === network)?.handleValues
            };
        });

        const agent = new MediaSubscription({
            id: this.agent?.id,
            label: this.name.value,
            state: this.status.value,
            shouldTerms: keywords.include,
            mustTerms: keywords.includeAll,
            mustNotTerms: keywords.exclude,
            isAgent: true,
            mediaTypeRequests: mediaTypeRequests,
            themeIds: this.themes.value,
            createdUserId: this.agent?.createdByUserId,
            createdDate: this.agent?.createdDate,
            modifiedUserId: this.agent?.modifiedByUserId,
            modifiedDate: this.agent?.modifiedDate,
            // twitter only
            samplePercentage: this.agent?.samplePercentage,
            countryCodes: this.agent?.countryCodes,
            geoCircles: this.agent?.geoCircles,
            verifiedAuthor: this.agent?.verifiedAuthor,
            languageFilter: this.agent?.languageFilter
        });

        return agent;
    }


    /**
     * Compares the name value to existing agent names
     * If not unique, an error object with the key 'duplicateName' will be returned
     * @TODO consider an async validator with agentService.isNameDuplicated$() also see NEP-32699
     */
    protected uniqueNameValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            let agentsList = this.agents;

            if (this.mode === AgentWidgetMode.edit) {
                agentsList = agentsList.filter(x => x.id !== this.agent.id);
            }

            const matchFound = agentsList.find(x => x.name?.toLocaleLowerCase() === control.value?.toLocaleLowerCase());
            return matchFound ? { duplicateName: true } : null;
        };
    }

    protected onlyNotKeywordValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const onlyNotKeywords = control.value.includeAll.length === 0
                && control.value.include.length === 0
                && control.value.exclude.length > 0;
            return onlyNotKeywords ? { onlyNotKeywords: true } : null;
        };
    }

    protected maxKeywordValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const maxReached =
                (control.value.includeAll.length +
                control.value.include.length +
                control.value.exclude.length) > 30;

            return maxReached ? { maxKeyword: true } : null;
        };
    }

    /**
     * Prevent hashtags keywords that contains symbols, excluding $, ^, +, =,`, ~
     */
    protected hashtagValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const hashtagValidRegex: RegExp = /^#[^\'\"\.,-\/#!%&\*;:{}\-()@\?><\\\|\[\]]*$/;
            const hashtag: RegExp = /^#/;

            const hashtagSymbol: boolean =
                control.value?.includeAll?.some(x => hashtag.test(x) && !hashtagValidRegex.test(x)) ||
                control.value?.include?.some(x => hashtag.test(x) && !hashtagValidRegex.test(x)) ||
                control.value?.exclude?.some(x => hashtag.test(x) && !hashtagValidRegex.test(x));

            return hashtagSymbol ? { hashtagSymbol: true } : null;
        };
    }

    /**
     * Prevent keywords that contains symbols ? or * when the twitter network is selected
     */
    protected keywordCharValidatorTwitter(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {
            const invalidTwitter: RegExp = /\?|\*/;
            const keywords = group.get('keywords').value;
            const netwoks = group.get('networks').value;

            const invalidChar: boolean =
                (keywords?.includeAll?.some(x => x.match(invalidTwitter)) ||
                keywords?.include?.some(x => x.match(invalidTwitter)) ||
                keywords?.exclude?.some(x => x.match(invalidTwitter))) &&
                !!netwoks.find(network => network === NetworkTypeEnum.twitter);

            return invalidChar ? { invalidCharKeywordTwitter: true } : null;
        };
    }

    /**
     * Prevent keywords that contain | symbol
     */
    protected keywordCharValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const containsPipe: RegExp = /\|/;

            const invalidChar: boolean =
                (control.value?.includeAll?.some(x => x.match(containsPipe)) ||
                control.value?.include?.some(x => x.match(containsPipe)) ||
                control.value?.exclude?.some(x => x.match(containsPipe)));

            return invalidChar ? { invalidCharKeyword: true } : null;
        };
    }

    protected noHandleOrKeywordValidator(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {
            const keywords = group.get('section.keywords').value;
            const handles = group.get('handles').value;

            const keywordsCount =
                keywords.includeAll.length +
                keywords.include.length +
                keywords.exclude.length;

            const noHandles: boolean = handles.every(x => x.handleValues.length === 0);

            return (keywordsCount === 0 && noHandles) ? { noHandleAndKeyword: true } : null;
        };
    }

    /**
     * for the status to be allowed to be active, we must have at least one theme defined for the agent
     */
    protected mustIncludeThemeIfActive(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (this.agentForm) {
                const mustIncludeThemeIfActive = control.value === StatusEnum.ACTIVE &&
                    (this.agentForm?.get('themes').value.length === 0);

                return mustIncludeThemeIfActive ? { mustIncludeThemeIfActive: true } : null;
            }
            return null;
        };
    }

    /**
     * Compares the handle value to the existing handles
     * If not unique, an error object with the key 'duplicateHandle' will be returned
     */
    protected uniqueHandleValidator(): ValidatorFn {
        return (control: AbstractControl<Handle>): ValidationErrors | null => {
            if (!control.value.handleValues || control.value?.handleValues.length === 0) {
                return null;
            }

            this.sanitizeHandles(control.value.type, control.value.handleValues);

            const notUnique = control.value.handleValues.length !== uniq(control.value.handleValues).length;

            return notUnique ? { duplicateHandle: true } : null;
        };
    }

    validateTheme(theme: Theme): boolean {
        return (theme.subscriptionIds?.length < Theme.MAX_NUM_AGENTS);
    }

    onThemeError(themeLabel: string) {
        this.messageService.add({
            severity: messageSeverityEnum.error,
            summary: 'Social.agentWidget.error.invalidTheme.heading',
            detail: this.localizationService.transform('Social.agentWidget.error.invalidTheme.message', {
                name: trimLongStringWithEllipsis(themeLabel, 40)
            })
        });
    }

    toggleExpanded() {
        this.handleExpanded = !this.handleExpanded;
    }

    selectedTabChange(tab: number) {
        this.selectedTabIndex = tab;
        if (this.selectedTabIndex === AgentWidgetTab.postVolumeEstimate) {
            this.showEstimate = true;
            this.getEstimate();
        } else {
            this.showEstimate = false;

        }
    }

    getEstimate() {
        const hasTwitter: boolean = this.networks.value.includes(NetworkTypeEnum.twitter);
        const twitterHandles: string[] = this.handles.value.find((handle: Handle) => handle.type === NetworkTypeEnum.twitter)?.handleValues;
        const noKeywords =
            this.keywords.value.includeAll.length === 0 &&
            this.keywords.value.include.length === 0 &&
            this.keywords.value.exclude.length === 0;
        this.getUsageEstimate(hasTwitter, twitterHandles, noKeywords);
    }

    scrollToBottom() {
        requestAnimationFrame(() => {
            this.scrollPanel.scrollTo(100000, 0);
        });
    }

    agentChanged() {
        this.isDirty = !this.getAgent().equals(this.cachedAgent);
        this.doesAgentContainX();
        this.doesAgentOnlyHaveNotKeywords();
    }

    doesAgentContainX() {
        this.containX = findIndex(this.getAgent()?.mediaTypeRequests, {mediaType: MediaSubscriptionMediaTypeEnum.twitter }) >= 0;
    }

    // if agent only contains NOT keywords, it breaks the backfill
    doesAgentOnlyHaveNotKeywords() {
        this.onlyNotKeywords = (this.getAgent()?.keywords?.not?.length > 0) &&
            (!this.getAgent()?.keywords?.and?.length && !this.getAgent()?.keywords?.or?.length);
    }

    protected getUsageEstimate(hasTwitter: boolean, twitterHandles: string[], noKeywords: boolean): void {
        if (!this.showEstimate) {

            return;
        }

        if (!hasTwitter || (noKeywords && !(twitterHandles?.length > 0))) {
            this.hasEstimate = false;
            this.hasSamplePosts = false;
            this.mentions = [];
            return;
        }

        this.loadingEstimate = true;
        this.mediaSubscriptionApiService.getAgentQuotaEstimate(this.getAgent().toApiModel()).subscribe({
            next: result => {
                this.hasEstimate = true;
                this.estimatedAgentUsageRange = result.quotaRange;
                this.estimatedAgentUsageCount = result.estimatedUsage / moment().daysInMonth() * (moment().daysInMonth() - moment().date());
                this.mentions = result.sampleMentions
                    .map(mention => new Mention(mention))
                    .sort((a, b) => b.datePublished.getTime() - a.datePublished.getTime());
                this.hasSamplePosts = this.mentions?.length > 0;
                this.generateQuotaEstimateChart();
                this.loadingEstimate = false;
            },
            error: error => {
                this.hasEstimate = false;
                this.hasSamplePosts = false;
                this.mentions = [];
                this.loadingEstimate = false;
            }
        });
    }

    protected generateQuotaEstimateChart(): void {
        const dataPoints = [];
        const totalQuota = (this.quotaInfo.maxCount + this.quotaInfo.additionalCount) || 1;
        const totalEstimate = this.totalUsageCount + this.estimatedTotalUsageCount + this.estimatedAgentUsageCount;
        const maxQuota = Math.max(totalQuota, totalEstimate);

        // total usage
        const totalUsageProportion = Math.min(100, this.totalUsageCount / maxQuota * 100);
        dataPoints.push({
            from: 0,
            to: totalUsageProportion,
            name: this.localizationService.transform('Social.agentWidget.estimateUsage.totalUsed'),
            fill: chartColors.agprMediumBlue,
            tickLength: 0
        });

        // total quota marker. This needs to be in the middle of the array so it does not affect corner radius in the chart
        if (totalEstimate <= totalQuota) {
            dataPoints.push({
                to: 100,
                name: abbreviateNumber(totalQuota)
            });
        } else {
            dataPoints.push({
                to: totalQuota / totalEstimate * 100,
                name: abbreviateNumber(totalQuota)
            });
        }

        // overall usage estimate
        let estimatedTotalUsageProportion = 0;
        estimatedTotalUsageProportion = Math.min(100, (this.totalUsageCount + this.estimatedTotalUsageCount) / maxQuota * 100);
        dataPoints.push({
            from: totalUsageProportion,
            to: estimatedTotalUsageProportion,
            name: this.localizationService.transform('Social.agentWidget.estimateUsage.overallUsageEstimate'),
            fill: chartColors.agprLightBlue,
            tickLength: 0
        });

        // post estimate usage
        let estimatedAgentUsageProportion = 0;
        estimatedAgentUsageProportion = Math.min(100, totalEstimate / maxQuota * 100);
        dataPoints.push({
            from: estimatedTotalUsageProportion,
            to: estimatedAgentUsageProportion,
            name: this.localizationService.transform('Social.agentWidget.estimateUsage.socialTopicUsageEstimate'),
            fill: chartColors.agprNegative,
            tickLength: 0
        });

        // remaining quota
        if (totalEstimate < maxQuota) {
            dataPoints.push({
                from: estimatedAgentUsageProportion,
                to: 100,
                name: this.localizationService.transform('Social.agentWidget.estimateUsage.remaingQuota'),
                fill: chartColors.agprNotAvailable,
                tickLength: 0
            });
        }

        const quotaEstimateSeries = {
            label: '',
            dataProvider: of(dataPoints)
        };

        const quotaEstimateChartStyles = new ChartStylesModel();
        quotaEstimateChartStyles.customPadding = {
            top: 35,
            right: 25,
            bottom: 5,
            left: 0
        };
        this.quotaEstimateChartOptions.styles = quotaEstimateChartStyles;

        this.quotaEstimateChart = new ProgressChartModel([quotaEstimateSeries], this.quotaEstimateChartOptions);
    }

    closeDialog(byPassLeaveGuard: boolean = false) {
        if (byPassLeaveGuard) {
            super.closeDialog();

            return;
        }

        this.agentChanged();
        this.showCanLeaveDialog().subscribe(
            (leave) => {
                if (leave) {
                    super.closeDialog();
                }
            }
        );
    }

    handleCancel(byPassLeaveGuard: boolean = false) {
        if (this.settingUpReplay) {
            this.settingUpReplay = false;
            this.enableForm();
            this.setAgentForm();
        } else {
            this.closeDialog(byPassLeaveGuard);
        }
    }

    setupReplay() {
        this.selectedTabIndex = AgentWidgetTab.additionalSearchSettings;
        this.networks.patchValue(this.networks.value.filter(network => network === NetworkTypeEnum.twitter));
        this.settingUpReplay = true;

        if (this.isCustomReplay) {
            this.enableForm();
        } else {
            this.disableForm();
        }
    }

    handleReplayType() {
        if (this.isCustomReplay) {
            this.enableForm();
        } else {
            this.setAgentForm();
            this.networks.patchValue(this.networks.value.filter(network => network === NetworkTypeEnum.twitter));
            this.disableForm();
        }
    }

    disableForm() {
        this.name.disable();
        this.general.disable();
        this.section.disable();
        this.networks.disable();
        this.keywords.disable();
        this.handles.disable();
        this.themes.disable();
        this.status.disable();
    }

    enableForm() {
        this.name.enable();
        this.general.enable();
        this.section.enable();
        this.networks.enable();
        this.keywords.enable();
        this.handles.enable();
        this.themes.enable();
        this.status.enable();
    }

    startReplay() {
        let backfillBeginDate: Moment;
        let backfillEndDate: Moment;

        if (this.replayDate.absoluteRange) {
            const range = DateQueryModel.makeAbsoluteRange(DateQueryModel.clone(this.replayDate), this.timeZone);

            backfillBeginDate = moment(range.absoluteRange.start).tz(this.timeZone, false);
            backfillEndDate = moment(range.absoluteRange.end).tz(this.timeZone, false);
        } else {
            backfillBeginDate = DateQueryModel.getStartDateForRelativeTimezone(this.replayDate.relativeTimezoneDateType, this.timeZone);
            backfillEndDate = DateQueryModel.getEndDateForRelativeTimezone(this.replayDate.relativeTimezoneDateType, this.timeZone);
        }

        let dateNow = moment().tz(this.timeZone);
        if (dateNow.isSameOrBefore(backfillEndDate)) {
            backfillEndDate = dateNow.subtract(1, 'minutes');
        }

        // have to reset it here because dateNow.setMinutes above
        dateNow = moment().tz(this.timeZone);
        if (dateNow.isSameOrBefore(backfillBeginDate)) {
            // we need a minimum of 10 seconds added here, so we will add 10 min for a buffer
            backfillBeginDate = dateNow.subtract(10, 'minutes');
        }

        if (backfillEndDate.isSameOrBefore(backfillBeginDate)) {
            // we need a minimum of 10 seconds added here, so we will add 10 min for a buffer
            backfillBeginDate = backfillEndDate.subtract(10, 'minutes');
        }

        this.coreDialogService.open(SocialTopicReplayDialogComponent, {
            inputs: {
                subscription: this.getAgent(),
                backfillBeginDate: backfillBeginDate.format(),
                backfillEndDate: backfillEndDate.format(),
                ignoreCustomSub: !this.isCustomReplay,
                quotaInfo: this.quotaInfo
            },
            outputs: {
                dialogClose: (requestSent: boolean) => {
                    if (requestSent) {
                        this.settingUpReplay = false;
                        this.enableForm();
                        this.setAgentForm();
                        this.agentHistory.getSocialTopicFromApi(this.agent.id);
                        this.selectedTabIndex = AgentWidgetTab.replayHistory;
                    }
                }
            }
        });
    }

    getModeratedHandles() {
        this.subscriptions.push(
            this.mediaSubscriptionApiService.getById(this.agent.id).subscribe(agent => {
                this.agent.mediaTypeRequests = agent.mediaTypeRequests;
                this.setAgentForm();
            })
        );
    }

    showCanLeaveDialog(forceClose: boolean = false): Observable<boolean> {
        if (!this.isDirty || forceClose) {

            return of(true);
        }
        return new Observable(
            (observer: Observer<boolean>) => {
                const io: ConfirmDialogModel = {
                    inputs: {
                        heading: 'DeactivateGuard.heading',
                        messageHtml: 'DeactivateGuard.message',
                        okLabel: 'continue',
                        id: 'AgentWidgetContinue',
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.isDirty = false;
                            }
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
